

































import { defineComponent, PropType, computed, useContext } from '@nuxtjs/composition-api';
import ProductCarouselSlider from '~/components/pages/Product/Detail/ProductCarouselSlider.vue';
import AppFavoriteIconButton from '~/components/partials/App/AppFavoriteIconButton.vue';
import { NoImageType } from '~/components/partials/App/AppImage.vue';
import { useMatchMedia } from '~/composables/useMatchMedia';
import { usePropSync } from '~/composables/usePropSync';
import { BOTTLE_400, BOTTLE_500 } from '~/constants/profilesForImage';
import { nonNullable } from '~/utils';
import { getProductVariant, getSpecialPricePromotion } from '~/utils/modules/products';
import { ProductShowECResponseSchema } from '~/types/modules/product';
import { ProductCategoryId } from '~/enums';

export default defineComponent({
  components: {
    AppFavoriteIconButton,
    ProductCarouselSlider,
  },
  props: {
    ecResponse: {
      type: Object as PropType<ProductShowECResponseSchema>,
      required: true,
    },
    isFavorite: {
      type: Boolean,
      required: true,
    },
  },
  setup(props) {
    const { i18n } = useContext();
    const { syncedIsFavorite } = usePropSync(props, 'isFavorite');
    const { isMobile } = useMatchMedia();

    const productName = computed(() => props.ecResponse.langs?.find((lang) => lang.id === 'ja')?.name);
    const isFreeShipping = computed(() =>
      props.ecResponse.product_variants?.find((variant) => variant.price_settings?.is_free_shipping)
    );
    const discountRate = computed(() => {
      const bestPricedVariant = getProductVariant(
        Number(props.ecResponse.best_priced_variant_id),
        props.ecResponse.product_variants
      );
      const bestPricedVariantPromotion = getSpecialPricePromotion(bestPricedVariant?.price_settings);
      return (
        bestPricedVariantPromotion?.discount_rate &&
        i18n.t('Item-03.promotion.specialPrice.label', { rate: bestPricedVariantPromotion.discount_rate }).toString()
      );
    });

    const noImageType = computed((): NoImageType => {
      switch (props.ecResponse.categories[0].id) {
        case ProductCategoryId.WINE_CELLAR:
          return 'cellar';
        case ProductCategoryId.FOOD:
          return 'food';
        case ProductCategoryId.WINE_GOODS:
          return 'goods';
        default:
          return 'product';
      }
    });

    const productImages = computed(() => {
      const mainImages = [props.ecResponse.product_image_1, props.ecResponse.product_image_2];
      const imagesExcludingMain = [
        props.ecResponse.product_image_3,
        props.ecResponse.product_image_4,
        props.ecResponse.product_image_5,
        props.ecResponse.product_image_6,
        props.ecResponse.product_image_7,
        props.ecResponse.product_image_8,
        props.ecResponse.product_image_9,
        props.ecResponse.product_image_10,
      ].filter(nonNullable);

      // 画像１枚も入ってない場合
      if (mainImages.filter(nonNullable).length === 0 && imagesExcludingMain.length === 0) {
        return ['']; // デフォルト画像を表示させるために空文字列が必要
      }

      // ドリンク系の場合はボトル画像(image_2)を優先する
      const category = props.ecResponse.categories[0].id;
      if (
        [
          ProductCategoryId.WINE,
          ProductCategoryId.OTHER_ALCOHOL,
          ProductCategoryId.NON_ALCOHOL,
          ProductCategoryId.OTHER_DRINK,
        ].includes(category)
      ) {
        const profile = isMobile.value ? BOTTLE_400 : BOTTLE_500;
        [mainImages[0], mainImages[1]] = [
          mainImages[1] ? mainImages[1] + profile : undefined,
          mainImages[0] ? mainImages[0] + profile : undefined,
        ];
      }

      // 空文字列・null・undefinedを除外する
      return mainImages.concat(imagesExcludingMain).filter(nonNullable);
    });

    const reviewMediaInfo = computed(() => {
      const info = props.ecResponse.highest_rated_review_medium_info;
      // 両方の情報揃ってない場合はトルツメ
      if (!info?.review_medium || !info?.score_sort) return;

      return {
        mark: info.review_medium,
        score: info.score_sort,
      };
    });

    return {
      productName,
      isFreeShipping,
      discountRate,
      noImageType,
      productImages,
      syncedIsFavorite,
      reviewMediaInfo,
    };
  },
});
